import { ChangeEvent } from 'react';

import { format } from 'date-fns';
import { UseFormSetValue } from 'react-hook-form';

import { validateClosingDate } from 'helpers';
import { FormItem, Investor, Option } from 'interfaces';
import {
  ExternalShareExchangeWatchValues,
  CreateExternalShareExchangeWithCompanyFormValues,
  ExternalShareExchangeFormValues,
} from 'interfaces/external-share-exchange.interface';
import { DatePickerInput, Input, SelectFormInput } from 'shared-components';
import NumberInput from 'shared-components/NumberInput';
import { getClosestWorkOrCurrentDate } from 'utils';
import { calculateNoOfExchangesShares } from 'utils/investments';

import { DAY_FORMAT_WITH_DASH } from './dateFormats';
import { INTEGER_PATTERN, REQUIRED_MESSAGE } from './global';
import { FieldTypes } from './shared';
import { FieldTooltipVariants } from './shared/input';

export enum ExternalShareExchangeFieldNames {
  CLASS_OF_SHARES = 'classOfShares',
  NO_OF_SHARES = 'noOfShares',
  COMPANY_PRICE = 'companyPrice',
  DAXIA_PRICE = 'fundSharePrice',
  NO_OF_EXCHANGED_SHARES = 'noExchangedShares',
  CLOSING_DATE = 'closingDate',
  ORIGINAL_INVESTMENT_PRICE = 'originalInvestmentPrice',
}

export const EXTERNAL_SHARE_EXCHANGE_PAGE_TITLE = 'Off Platform Share Exchange';

export const SHARE_EXCHANGE_FORM_FIELDS = (
  index: number,
  setValue: UseFormSetValue<ExternalShareExchangeFormValues>,
  classOfSharesOptions: Option[],
  watchValues: ExternalShareExchangeWatchValues,
  maxDate?: boolean,
): FormItem[] => {
  const shareExchangesPrefix: `exchanges.${number}` = `exchanges.${index}`;
  const { noOfShares, companyPrice, fundSharePrice } = watchValues;

  const useSelectForClassOfShares = classOfSharesOptions?.length > 0;

  return [
    ...(useSelectForClassOfShares
      ? [
          {
            name: `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.CLASS_OF_SHARES}`,
            label: 'Class of shares',
            placeholder: 'Class of shares',
            type: FieldTypes.SELECT,
            component: SelectFormInput,
            required: true,
            options: classOfSharesOptions,
            validation: { required: REQUIRED_MESSAGE },
            className: 'col-span-full md:col-span-4 mb-0',
            onChange: (e: ChangeEvent<HTMLSelectElement>) => {
              setValue(
                `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.CLASS_OF_SHARES}`,
                e.target.value as unknown as Option,
              );
            },
          },
        ]
      : []),
    ...(!useSelectForClassOfShares
      ? [
          {
            name: `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.CLASS_OF_SHARES}`,
            label: 'Class of shares',
            placeholder: 'Class of shares',
            type: FieldTypes.TEXT,
            component: Input,
            required: true,
            validation: { required: REQUIRED_MESSAGE },
            className: 'col-span-full md:col-span-4 mb-0',
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
              setValue(`${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.CLASS_OF_SHARES}`, e.target.value);
            },
          },
        ]
      : []),
    {
      name: `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.NO_OF_SHARES}`,
      label: 'No. shares',
      placeholder: 'No. shares',
      tooltipVariant: FieldTooltipVariants.WITH_ICON,
      tooltipContent: 'Please enter this figure if known. If unknown, you can amend / add the number at a later time',
      type: FieldTypes.NUMBER,
      component: NumberInput,
      className: 'col-span-full md:col-span-4 mb-0',
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(INTEGER_PATTERN, '');
        setValue(`${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.NO_OF_SHARES}`, value);
        if (value && companyPrice && fundSharePrice) {
          setValue(
            `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.NO_OF_EXCHANGED_SHARES}`,
            calculateNoOfExchangesShares({ noOfShares: value, companyPrice, fundSharePrice }),
          );
        }
      },
    },
    {
      name: `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.ORIGINAL_INVESTMENT_PRICE}`,
      label: 'Total initial investment value',
      placeholder: 'Total initial investment value',
      type: FieldTypes.NUMBER,
      component: NumberInput,
      className: 'col-span-full md:col-span-4 mb-0',
      fractionLength: 50,
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        setValue(
          `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.ORIGINAL_INVESTMENT_PRICE}`,
          e.target.value,
        );
      },
    },
    {
      name: `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.CLOSING_DATE}`,
      label: 'Closing date',
      placeholder: 'Closing date',
      type: FieldTypes.DATE,
      component: DatePickerInput,
      required: true,
      className: 'col-span-full md:col-span-6 2lg:col-span-3 mb-0',
      max: maxDate ? new Date() : undefined,
      tooltipVariant: FieldTooltipVariants.WITH_ICON,
      validation: {
        required: REQUIRED_MESSAGE,
        validate: (closingDateValue: string) => {
          return validateClosingDate(closingDateValue, false, true);
        },
      },
      disabled: shareExchangesPrefix !== 'exchanges.0',
      onChange: (e) => {
        const calculatedDate = getClosestWorkOrCurrentDate(new Date(e.target.value));
        setValue(
          `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.CLOSING_DATE}`,
          format(calculatedDate, DAY_FORMAT_WITH_DASH),
        );
      },
    },

    {
      name: `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.COMPANY_PRICE}`,
      label: 'Price per share',
      placeholder: 'Price per share',
      type: FieldTypes.TEXT,
      tooltipVariant: FieldTooltipVariants.WITH_ICON,
      tooltipContent: 'This is the offer price per share that Daxia are willing to pay',
      component: Input,
      validation: { required: REQUIRED_MESSAGE },
      className: 'col-span-full md:col-span-6 2lg:col-span-3 mb-0',
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        setValue(`${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.COMPANY_PRICE}`, e.target.value);
        if (noOfShares && e.target.value && fundSharePrice) {
          setValue(
            `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.NO_OF_EXCHANGED_SHARES}`,
            calculateNoOfExchangesShares({ noOfShares, companyPrice: e.target.value, fundSharePrice }),
          );
        }
      },
    },
    {
      name: `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.DAXIA_PRICE}`,
      label: 'Daxia price per share',
      placeholder: 'Daxia price per share',
      type: FieldTypes.TEXT,
      component: Input,
      required: true,
      validation: { required: REQUIRED_MESSAGE },
      className: 'col-span-full md:col-span-6 2lg:col-span-3 mb-0',
      onChange: (e: ChangeEvent<HTMLInputElement>) => {
        setValue(`${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.DAXIA_PRICE}`, e.target.value);
        if (noOfShares && companyPrice && e.target.value) {
          setValue(
            `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.NO_OF_EXCHANGED_SHARES}`,
            calculateNoOfExchangesShares({ noOfShares, companyPrice, fundSharePrice: e.target.value }),
          );
        }
      },
    },
    {
      name: `${shareExchangesPrefix}.${ExternalShareExchangeFieldNames.NO_OF_EXCHANGED_SHARES}`,
      label: 'Daxia shares to be received',
      placeholder: 'Daxia shares to be received',
      type: FieldTypes.NUMBER,
      tooltipVariant: FieldTooltipVariants.WITH_ICON,
      tooltipContent: 'This is the number of shares in Daxia you will receive in return for your share',
      component: NumberInput,
      required: true,
      disabled: true,
      validation: { required: REQUIRED_MESSAGE },
      className: 'col-span-full md:col-span-6 2lg:col-span-3 mb-0',
    },
  ];
};

export enum CreateExternalShareExchangeWithCompanyFieldNames {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  INVESTOR_ID = 'investorId',
  EMAIL = 'email',
}

export const SHARE_EXCHANGE_INVESTOR_SELECTORS_FIELDS = (
  emailOptions: Option[],
  setValue: UseFormSetValue<CreateExternalShareExchangeWithCompanyFormValues>,
  investors: Investor[] | null,
  isEditable?: boolean,
): FormItem[] => {
  return [
    {
      name: `${CreateExternalShareExchangeWithCompanyFieldNames.FIRST_NAME}`,
      label: 'First Name',
      placeholder: 'First Name',
      disabled: isEditable,
      component: Input,
      className: 'col-span-full sm:col-span-2 md:col-span-1',
      required: true,
      validation: { required: REQUIRED_MESSAGE },
      fractionLength: 50,
    },
    {
      name: `${CreateExternalShareExchangeWithCompanyFieldNames.LAST_NAME}`,
      label: 'Last Name',
      placeholder: 'Last Name',
      disabled: isEditable,
      component: Input,
      className: 'col-span-full sm:col-span-2 md:col-span-1',
      required: true,
      validation: { required: REQUIRED_MESSAGE },
      fractionLength: 50,
    },
  ];
};
